import React from 'react'
import cx from 'classnames'
import { useDevice } from 'device'
import { Helmet } from 'react-helmet-async'

import { usePathname } from 'router'
import links from 'links'

import { Bone, WidthContainer } from 'components/layout'
import { HeroMediaBlock, Image, Text, type TextColor, type TextStyle } from 'components/dataDisplay'

import SubscribeButton from 'compositions/buttons/SubscribeButton/SubscribeButton'
import StyledTimer, { BgColor, type Size } from 'compositions/StyledTimer/StyledTimer'

import type { HomePageCustomBlockButton } from 'pages/landing/View/View'

import GetStartedButton from '../GetStartedButton/GetStartedButton'
import SubscriptionOfferBadge from './components/SubscriptionOfferBadge/SubscriptionOfferBadge'

import messages from './messages'

import desktopImage from './images/desktop-hero.jpg?src-loader&size'
import mobileImage from './images/mobile-hero.jpg?src-loader&size'
import mobileImageWithCase from './images/mobile-hero-with-case.jpg?src-loader&size'


type CustomHeroTextBlock = {
  className?: string
  style?: TextStyle
  color?: TextColor
  message?: string | Intl.Message
}

export type HeroSectionProps = {
  offerType: SubscriptionModule.OfferType
  price: string
  priceWithDiscount: string
  extraShippingPrice: string
  discount: string
  customBlocks: {
    preTitle?: CustomHeroTextBlock
    title?: CustomHeroTextBlock
    text?: CustomHeroTextBlock
    note?: CustomHeroTextBlock
    afterButtonText?: CustomHeroTextBlock
    timer?: {
      size?: Size
      className?: string
      withLabel: boolean
      titleClassname?: string
      titleStyle?: TextStyle
      titleColor?: TextColor
      backgroundColor?: BgColor
      countdownColor?: TextColor
      captionColor?: TextColor
    }
    logo?: string
    background?: {
      src: string
      width: number
      height: number
      aspect: number
    }
    button?: HomePageCustomBlockButton
  }
  isFetching: boolean
  isTimerEnabled?: boolean
  startButtonMessage?: string | Intl.Message
  noteMessage?: string | Intl.Message
  offerBadge?: React.ReactNode
}

const messagesByOffer: Record<SubscriptionModule.OfferType, { title: Intl.Message, text: Intl.Message, note?: Intl.Message }> = {
  freeTrial: messages.freeTrial,
  free2ndMonth: messages.freeMonth,
  freeProduct: messages.freeProduct,
  discount: messages.discount,
}

const HeroSection: React.FunctionComponent<HeroSectionProps> = (props) => {
  const {
    customBlocks,
    discount,
    extraShippingPrice,
    isFetching,
    offerType,
    price,
    priceWithDiscount,
    isTimerEnabled,
    startButtonMessage,
    offerBadge,
  } = props

  const { isMobile, isDesktop } = useDevice()
  const pathname = usePathname()

  const withOfferBadge = offerType && pathname !== links.nift

  const withTimer = Boolean(offerType && isTimerEnabled)

  let heroImage

  if (customBlocks?.background) {
    heroImage = customBlocks.background
  }
  else {
    if (isMobile) {
      heroImage = withOfferBadge ? mobileImageWithCase : mobileImage
    }
    else {
      heroImage = desktopImage
    }
  }

  let preTitle
  let titleMessage
  let textMessage
  let noteMessage
  let afterButtonText

  if (customBlocks) {
    preTitle = customBlocks.preTitle?.message
    titleMessage = customBlocks.title?.message
    if (customBlocks.text?.message) {
      textMessage = typeof customBlocks.text?.message === 'string' ? customBlocks.text?.message : {
        ...customBlocks.text?.message,
        values: {
          price,
          priceWithDiscount,
          ...customBlocks.text?.message.values,
        },
      }
    }
    noteMessage = customBlocks.note?.message
    afterButtonText = customBlocks.afterButtonText?.message
  }
  else {
    titleMessage = {
      ...messagesByOffer[offerType]?.title || messages.noDiscount.title,
      values: {
        discount,
        price,
      },
    }

    if (messagesByOffer[offerType]?.text) {
      textMessage = {
        ...messagesByOffer[offerType]?.text,
        values: {
          price,
          extraShippingPrice,
          priceWithDiscount: priceWithDiscount || price,
        },
      }
    }

    if (messagesByOffer[offerType]?.note) {
      noteMessage = {
        ...messagesByOffer[offerType]?.note,
        values: {
          priceWithDiscount: priceWithDiscount || price,
          withBr: !withTimer,
        },
      }
    }
  }

  const withLogo = Boolean(customBlocks?.logo)
  const isTimerWithLabel = (
    customBlocks?.timer === undefined || customBlocks?.timer?.withLabel
  )

  const timerClassName = customBlocks?.timer?.className ?? (!noteMessage || !isTimerWithLabel ? 'mt-24' : null)
  const timerBackgroundColor = customBlocks?.timer?.backgroundColor ?? undefined
  const timerCountdownColor = customBlocks?.timer?.countdownColor ?? undefined
  const timerCaptionColor = customBlocks?.timer?.captionColor ?? undefined
  const timerSize = customBlocks?.timer?.size ?? 56
  const timerTitleClassname = customBlocks?.timer?.titleClassname ?? 'mb-12'
  const timerTitleStyle = customBlocks?.timer?.titleStyle ?? (isMobile ? 'p5' : 'p1')
  const timerTitleColor = customBlocks?.timer?.titleColor ?? 'black'

  const preTitleStyle = customBlocks?.preTitle?.style ?? (isMobile ? 'p1' : 'sh3')
  const titleStyle = customBlocks?.title?.style ?? (isMobile ? 'h3' : 'h2')
  const textStyle = customBlocks?.text?.style ?? (isMobile ? 'p1' : 'sh3')
  const noteStyle = customBlocks?.note?.style ?? (isMobile ? 't1' : 'sh3')
  const afterButtonTextStyle = customBlocks?.afterButtonText?.style ?? 'sh6'

  const preTitleColor = customBlocks?.preTitle?.color ?? undefined
  const titleColor = customBlocks?.title?.color ?? undefined
  const textColor = customBlocks?.text?.color ?? undefined
  const noteColor = customBlocks?.note?.color ?? 'white'
  const afterButtonTextColor = customBlocks?.afterButtonText?.color ?? undefined

  const preTitleClassname = customBlocks?.preTitle?.className ?? 'mb-20 text-white'
  const titleClassname = customBlocks?.title?.className ?? 'max-w-464'
  const textClassname = customBlocks?.text?.className ?? (isMobile ? 'mt-8' : 'mt-24 max-w-464 pr-64')
  const noteClassname = customBlocks?.note?.className ?? (isMobile ? 'mt-8' : cx('mt-16 max-w-464 pr-64', withTimer ? 'whitespace-nowrap' : 'whitespace-pre-line'))
  const afterButtonTextClassname = customBlocks?.afterButtonText?.className ?? (isMobile ? 'mt-8' : 'mt-20')

  const ButtonComponent = customBlocks?.button?.actionType === 'subscribe' ? SubscribeButton : GetStartedButton
  const buttonProps = {
    title: startButtonMessage,
    size: 56 as const,
    width: 280 as const,
    className: withTimer ? 'mt-32' : 'mt-56',
    ...customBlocks?.button,
  }

  let mobileMinHeight
  if (withLogo) {
    mobileMinHeight = '760rem'
  }
  else if (withTimer) {
    mobileMinHeight = '686rem'
  }
  else {
    mobileMinHeight = '608rem'
  }

  // inline without zina to improve page load speed (LCP)
  const style = {
    background: `url(${heroImage.src})`,
    backgroundPosition: isMobile ? 'bottom' : undefined,
    backgroundSize: 'cover',
    minHeight: isMobile ? mobileMinHeight : '768rem',
    backgroundRepeat: 'no-repeat',
  }

  const containerClassName = isMobile
    ? 'flex w-full bg-gold-30 pt-24 text-center'
    : 'flex w-full items-center justify-start bg-gold-30'


  return (
    <HeroMediaBlock className="relative overflow-hidden" data-testid="heroSection">
      <Helmet>
        <link rel="preload" href={heroImage.src} as="image" />
      </Helmet>
      <div className={containerClassName} style={style}>
        {
          isFetching ? (
            <WidthContainer className={isMobile ? null : 'w-full max-w-464'}>
              <Bone
                className={cx('w-full max-w-336 rounded', isMobile && 'mx-auto')}
                h={isMobile ? 20 : 40}
                bgColor="gray-70"
              />
              <Bone
                className={cx('mt-12 rounded', isMobile && 'mx-auto')}
                w={isMobile ? 200 : 400}
                h={isMobile ? 20 : 40}
                bgColor="gray-70"
              />
              <Bone
                className={cx(isMobile ? 'mx-auto mt-16' : 'mt-32')}
                w={isMobile ? 240 : 280}
                h={isMobile ? 38 : 56}
                bgColor="gray-70"
              />
            </WidthContainer>
          ) : (
            <WidthContainer ignore={isMobile} className={cx('w-full', isMobile && 'flex flex-col px-16')}>
              {
                withLogo && (
                  <Image className={isMobile ? 'mb-16 max-h-80 self-center' : 'mb-24 max-h-[110rem]'} src={customBlocks?.logo} />
                )
              }
              {
                Boolean(preTitle) && (
                  <Text
                    className={preTitleClassname}
                    message={preTitle}
                    style={preTitleStyle}
                    color={preTitleColor}
                    html
                  />
                )
              }
              <Text
                className={titleClassname}
                message={titleMessage}
                style={titleStyle}
                color={titleColor}
                html
              />
              {
                Boolean(textMessage) && (
                  <Text
                    className={textClassname}
                    message={textMessage}
                    style={textStyle}
                    color={textColor}
                    html
                  />
                )
              }
              {
                Boolean(noteMessage) && (
                  <Text
                    className={noteClassname}
                    message={noteMessage}
                    style={noteStyle}
                    color={noteColor}
                    html
                  />
                )
              }
              {
                withTimer && (
                  <StyledTimer
                    className={timerClassName}
                    size={timerSize}
                    backgroundColor={timerBackgroundColor}
                    countdownColor={timerCountdownColor}
                    captionColor={timerCaptionColor}
                    withTitle={isTimerWithLabel}
                    titleClassname={timerTitleClassname}
                    titleStyle={timerTitleStyle}
                    titleColor={timerTitleColor}
                  />
                )
              }
              {
                isDesktop && (
                  <ButtonComponent
                    {...buttonProps}
                    placement="Hero banner"
                  />
                )
              }
              {
                Boolean(afterButtonText) && (
                  <Text
                    className={afterButtonTextClassname}
                    message={afterButtonText}
                    style={afterButtonTextStyle}
                    color={afterButtonTextColor}
                    html
                  />
                )
              }
              {
                withOfferBadge && !offerBadge && (
                  <SubscriptionOfferBadge
                    className={cx('absolute', isMobile ? 'bottom-16 right-12' : 'bottom-88 left-[690rem]')}
                    offerType={offerType}
                    discount={discount}
                  />
                )
              }
              {offerBadge}
            </WidthContainer>
          )
        }
      </div>
    </HeroMediaBlock>
  )
}


export default React.memo(HeroSection)
